import React, { useEffect, useState } from "react";
import { Textarea, TextInput, Button, Notification } from "@mantine/core";
import "./OrganizerSettings.scss";
import {
  IconCloudUpload,
  IconEye,
  IconPlus,
  IconDeviceFloppy,
} from "@tabler/icons-react";
import type { AdditionalQuestionsConfig, Question } from "@/types";
import { useAdditionalQuestionsConfig } from "@utils/hooks/useAdditionalQuestionsConfig.ts";
import OrganizerSettingsSkeleton from "./OrganizerSettingsSkeleton";
import PreviewQuestions from "./PreviewQuestions/PreviewQuestions";
import QuestionSettings from "./QuestionSettings";
import ShortUniqueId from "short-unique-id";

interface OrganizerSettingsProps {
  setHasUnsavedChanges: (hasChanges: boolean) => void;
}

/**
 * The OrganizerSettings component is used to manage the settings for the tax organizer.
 * The component is used in the Tax Pro Dashboard and is responsible for rendering the settings
 * for the tax organizer, including the form type, additional questions section, and notification
 * for when the settings are updated.
 *
 * The component takes in the following props:
 * - updateTaxProInfo: a function that is called when the tax pro updates their profile information.
 * - setHasUnsavedChanges: a function that is called when the user makes changes to the settings.
 *
 * The component renders a header, the form type, and the additional questions section. The
 * additional questions section renders a title and description for the section, and a list of
 * questions. Each question renders a title, description, and a question type (shortInput,
 * longInput, or docUpload). The component also renders a notification when the settings are
 * updated.
 */
const OrganizerSettings: React.FC<OrganizerSettingsProps> = ({
  setHasUnsavedChanges,
}) => {
  const {
    data: additionalQuestionsConfig,
    isLoading: isLoadingConfig,
    error: configError,
    updateConfig,
    isUpdating,
  } = useAdditionalQuestionsConfig();

  const [localChanges, setLocalChanges] = useState<any>(() => ({
    title: "",
    description: "",
    questions: [],
  }));

  const [savedQuestions, setSavedQuestions] = useState<any[]>([]);

  const [focusedQuestionIndex, setFocusedQuestionIndex] = useState<
    string | null
  >(null);
  const [editingPlaceholder, setEditingPlaceholder] = useState<string | null>(
    null
  );

  const [openPreview, setOpenPreview] = useState(false);

  const [notification, setNotification] = useState<{
    title: string;
    message: string;
    color: string;
  } | null>(null);

  const [hasUnsavedChangesLocal, setHasUnsavedChangesLocal] = useState(false);

  // Wrapper function to update both parent and local unsaved changes state
  const handleUnsavedChanges = (hasChanges: boolean) => {
    setHasUnsavedChanges(hasChanges);
    setHasUnsavedChangesLocal(hasChanges);
  };

  const isQuestionValid = (question: Question): boolean => {
    return !!question?.title && !!question?.type && !!question?.type.name;
  };

  const isQuestionModified = (question: Question): boolean => {
    const savedQuestion = savedQuestions.find((q) => q.id === question.id);
    if (!savedQuestion) {
      // New question (not in saved list)
      return true;
    }
    // Compare question fields
    return JSON.stringify(savedQuestion) !== JSON.stringify(question);
  };

  // Update localChanges when data is successfully fetched
  useEffect(() => {
    if (additionalQuestionsConfig?.content) {
      const questions = additionalQuestionsConfig.content.questions ?? [];
      setLocalChanges(
        (prev: { title: any; description: any; questions: any }) => ({
          title: additionalQuestionsConfig.content.title ?? prev.title,
          description:
            additionalQuestionsConfig.content.description ?? prev.description,
          questions: questions,
        })
      );
      // Save a deep copy of the original questions for comparison
      setSavedQuestions(JSON.parse(JSON.stringify(questions)));
    }
  }, [additionalQuestionsConfig]);

  // Show loading skeleton while fetching config
  if (isLoadingConfig) {
    return <OrganizerSettingsSkeleton />;
  }

  // Show error for config fetch
  if (configError) {
    return <div>Error loading configuration: {configError?.message}</div>;
  }

  const handleConfigChange = (
    field: keyof AdditionalQuestionsConfig,
    value: string
  ) => {
    setLocalChanges((prev: any) => ({
      ...prev,
      [field]: value,
    }));
    handleUnsavedChanges(true);
  };

  const handleQuestionChange = (
    id: string | undefined,
    field: keyof Question,
    value: any
  ) => {
    setLocalChanges((prev: { questions: any[] }) => {
      const updatedQuestions = prev.questions.map((question) =>
        question.id === id ? { ...question, [field]: value } : question
      );
      return { ...prev, questions: updatedQuestions };
    });
    handleUnsavedChanges(true);
  };

  const addQuestion = () => {
    setLocalChanges((prev: { questions: any }) => ({
      ...prev,
      questions: [
        ...(prev?.questions || []),
        {
          id: new ShortUniqueId({ length: 10 }).rnd(), //get unique id
          title: "",
          description: "",
          type: { name: "shortInput" },
          isRequired: true,
          placeholder: "Your answer here",
        },
      ],
    }));
    handleUnsavedChanges(true);
  };

  const handleSaveQuestion = async (questionId: string) => {
    try {
      const question = localChanges.questions.find(
        (q: Question) => q.id === questionId
      );

      if (!question) {
        return;
      }

      // Validate the question
      if (!isQuestionValid(question)) {
        setNotification({
          title: "Warning",
          message:
            "Please fill in the question title and select a type before saving.",
          color: "yellow",
        });
        return;
      }

      // Save the entire config (including this question)
      const success = await updateConfig(localChanges);

      if (success) {
        setNotification({
          title: "Success",
          message: "Question saved successfully",
          color: "green",
        });
        handleUnsavedChanges(false);
        // Update saved questions after successful save
        setSavedQuestions(JSON.parse(JSON.stringify(localChanges.questions)));
      } else {
        throw new Error("Failed to save question");
      }
    } catch (error) {
      console.error("Error saving question:", error);
      setNotification({
        title: "Error",
        message: "Failed to save question. Please try again.",
        color: "red",
      });
    }
  };

  const handleDeleteQuestion = async (id: string) => {
    // Show confirmation dialog
    const confirmed = window.confirm(
      "Are you sure you want to delete this question? This action cannot be undone."
    );

    if (!confirmed) {
      return;
    }

    try {
      // Remove question from local state
      const updatedQuestions = localChanges.questions.filter(
        (question: Question) => question.id !== id
      );

      const updatedConfig = {
        ...localChanges,
        questions: updatedQuestions,
      };

      // Immediately save to backend
      const success = await updateConfig(updatedConfig);

      if (success) {
        // Update local state
        setLocalChanges(updatedConfig);

        setNotification({
          title: "Success",
          message: "Question deleted successfully",
          color: "green",
        });

        handleUnsavedChanges(false);
        // Update saved questions after successful delete
        setSavedQuestions(JSON.parse(JSON.stringify(updatedQuestions)));
      } else {
        throw new Error("Failed to delete question");
      }
    } catch (error) {
      console.error("Error deleting question:", error);
      setNotification({
        title: "Error",
        message: "Failed to delete question. Please try again.",
        color: "red",
      });
    }
  };

  const handleSave = async () => {
    if (localChanges) {
      try {
        const validQuestions = localChanges.questions.filter(isQuestionValid);
        const invalidQuestion =
          validQuestions.length !== localChanges.questions.length;
        const updatedConfig = {
          ...localChanges,
          questions: localChanges.questions.filter(isQuestionValid),
        };
        setLocalChanges(updatedConfig);
        const success = await updateConfig(updatedConfig);
        if (success) {
          if (invalidQuestion) {
            setNotification({
              title: "Warning",
              message:
                "Please make sure all questions are valid, i.e. have a title and type.",
              color: "yellow",
            });
          } else {
            setNotification({
              title: "Success",
              message: "Additional questions updated successfully",
              color: "green",
            });
            handleUnsavedChanges(false);
            // Update saved questions after successful save
            setSavedQuestions(
              JSON.parse(JSON.stringify(updatedConfig.questions))
            );
          }
        } else {
          throw new Error("Failed to update additional questions");
        }
      } catch (error) {
        console.error("Error updating additional questions:", error);
        setNotification({
          title: "Error",
          message: "Failed to update additional questions. Please try again.",
          color: "red",
        });
      }
    }
  };

  const renderQuestionByType = (question: any) => {
    const isEditing = editingPlaceholder === question?.id;
    const commonProps = {
      value: isEditing ? question.placeholder : "",
      placeholder: isEditing
        ? "Editing placeholder"
        : (question.placeholder ?? "Edit placeholder"),
      onFocus: () => setEditingPlaceholder(question?.id),
      onBlur: () => setEditingPlaceholder(null),
      onChange: (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      ) =>
        handleQuestionChange(
          question?.id,
          "placeholder",
          event.currentTarget.value
        ),
      readOnly: !isEditing,
    };
    switch (question.type.name) {
      case "shortInput":
        return (
          <TextInput
            {...commonProps}
            variant="filled"
            data-intercom-target="question-answer"
            className="question-answer"
          />
        );
      case "longInput":
        return (
          <Textarea
            {...commonProps}
            variant="filled"
            data-intercom-target="question-answer"
            className="question-answer"
          />
        );
      case "docUpload":
        return (
          <div
            className="upload-indicator"
            data-intercom-target="question-answer"
          >
            <IconCloudUpload />
            <p>Upload File</p>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="organizer-settings">
      {notification && (
        <Notification
          title={notification.title}
          color={notification.color}
          onClose={() => setNotification(null)}
          withCloseButton
        >
          {notification.message}
        </Notification>
      )}
      <div className="header-settings">
        <Button
          size="md"
          color="#0077CC"
          radius="0.5rem"
          onClick={handleSave}
          disabled={!localChanges || isUpdating}
          loading={isUpdating}
        >
          {isUpdating ? "Saving..." : "Save"}
        </Button>
      </div>

      <div className="additional-questions-section">
        <div className="left-section-title">
          <div className="title-description">
            <h3>Additional Questions Section</h3>
            <p>
              You can add a section to ask your clients additional questions on
              the organizer.
            </p>
          </div>
          <Button
            className="preview-btn"
            variant="transparent"
            leftSection={<IconEye />}
            onClick={() => setOpenPreview(true)}
          >
            Preview
          </Button>
        </div>

        <div className="title-description">
          <div>
            <h3>Title and Description</h3>
            <p>You can add your own title and description for this section.</p>
          </div>
          <TextInput
            placeholder="Title"
            label="Title"
            data-intercom-target="additional-questions-title"
            value={localChanges.title}
            onChange={(event) =>
              handleConfigChange("title", event.currentTarget.value)
            }
          />
          <Textarea
            placeholder="Description"
            label="Description"
            minRows={4}
            maxRows={4}
            autosize
            data-intercom-target="additional-questions-description"
            mb="lg"
            value={localChanges.description}
            onChange={(event) =>
              handleConfigChange("description", event.currentTarget.value)
            }
          />
        </div>

        <div className="questions">
          <span className="add-question">
            <h3>Questions</h3>
            <Button
              leftSection={<IconPlus />}
              variant="outline"
              onClick={addQuestion}
              data-intercom-target="add-question"
            >
              Add a Question
            </Button>
          </span>
          {hasUnsavedChangesLocal && (
            <p
              style={{
                color: "#f59e0b",
                fontSize: "0.875rem",
                marginTop: "0.5rem",
                fontStyle: "italic",
              }}
            >
              Questions with an orange border have unsaved changes. Click Save
              to keep your changes.
            </p>
          )}
          {localChanges?.questions?.map((question: Question) => (
            <div
              key={question.id as string}
              className={`question ${
                focusedQuestionIndex === question.id ? "focused" : ""
              } ${isQuestionModified(question) ? "modified" : ""}`}
              data-intercom-target="question"
              onClick={() => setFocusedQuestionIndex(question.id as string)}
            >
              <div className="left-section">
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                  }}
                >
                  <TextInput
                    placeholder="Your question here"
                    variant="transparent"
                    className="question-title"
                    data-intercom-target="question-title"
                    value={question.title}
                    onChange={(event) =>
                      handleQuestionChange(
                        question.id as string,
                        "title",
                        event.currentTarget.value
                      )
                    }
                    style={{ flex: 1 }}
                  />
                  {question.isRequired && (
                    <span
                      style={{
                        backgroundColor: "#0077CC",
                        color: "white",
                        padding: "0.125rem 0.5rem",
                        borderRadius: "0.25rem",
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        whiteSpace: "nowrap",
                      }}
                    >
                      Required
                    </span>
                  )}
                  {isQuestionModified(question) && (
                    <Button
                      size="xs"
                      variant="filled"
                      color="#0077CC"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSaveQuestion(question.id as string);
                      }}
                      leftSection={<IconDeviceFloppy size={14} />}
                      style={{ minWidth: "fit-content" }}
                      loading={isUpdating}
                    >
                      Save
                    </Button>
                  )}
                </div>
                <TextInput
                  placeholder="Description (Optional)"
                  variant="transparent"
                  className="question-description"
                  data-intercom-target="question-description"
                  value={question.description}
                  onChange={(event) =>
                    handleQuestionChange(
                      question?.id as string,
                      "description",
                      event.currentTarget.value
                    )
                  }
                />
                {renderQuestionByType(question)}
              </div>
              {focusedQuestionIndex === question?.id && (
                <QuestionSettings
                  id={question?.id as string}
                  handleQuestionChange={handleQuestionChange}
                  localChanges={localChanges}
                  setLocalChanges={setLocalChanges}
                  setHasUnsavedChanges={handleUnsavedChanges}
                  handleDeleteQuestion={handleDeleteQuestion}
                />
              )}
            </div>
          ))}
        </div>
      </div>
      <PreviewQuestions
        additionalQuestionsConfig={localChanges}
        opened={openPreview}
        setOpened={setOpenPreview}
      />
    </div>
  );
};

export default OrganizerSettings;
